import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, School, ArrowLeft } from 'lucide-react';
import { useForecastScenarios } from '../hooks/useForecast';
import { applyFerEnsembleTemplate } from '../utils/forecastTemplates';

export default function ForecastNewPage() {
  const navigate = useNavigate();
  const { createScenario } = useForecastScenarios();

  const [step, setStep] = useState<'choose' | 'configure'>('choose');
  const [templateType, setTemplateType] = useState<'blank' | 'fer_ensemble'>('blank');
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('2024-09-01');
  const [yearsCount, setYearsCount] = useState(5);
  const [creating, setCreating] = useState(false);

  const handleChooseTemplate = (type: 'blank' | 'fer_ensemble') => {
    setTemplateType(type);
    if (type === 'fer_ensemble') {
      setName('Budget prévisionnel Fer Ensemble');
      setStartDate('2024-09-01');
    } else {
      setName('');
    }
    setStep('configure');
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      alert('Veuillez saisir un nom pour le scénario');
      return;
    }

    setCreating(true);
    try {
      const scenario = await createScenario({
        name: name.trim(),
        start_date: startDate,
        years_count: yearsCount,
        currency: 'EUR'
      });

      if (!scenario) {
        throw new Error('Scenario creation failed');
      }

      // Si template Fer Ensemble, appliquer les données
      if (templateType === 'fer_ensemble') {
        await applyFerEnsembleTemplate(scenario.id);
      }

      navigate(`/app/forecast/${scenario.id}/edit`);
    } catch (error) {
      console.error('Error creating scenario:', error);
      alert('Erreur lors de la création du scénario');
      setCreating(false);
    }
  };

  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/app/forecast')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft size={20} />
            Retour
          </button>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Créer un nouveau scénario
          </h1>
          <p className="text-gray-600 mb-8">
            Choisissez comment démarrer votre budget prévisionnel
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => handleChooseTemplate('blank')}
              className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-blue-500 transition-all p-8 text-left group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                  <FileText size={32} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Scénario vide</h3>
              </div>
              <p className="text-gray-600">
                Créez un scénario vierge et définissez vous-même toutes vos lignes de charges
                et de produits.
              </p>
              <div className="mt-6 text-blue-600 font-medium flex items-center gap-2">
                Créer un scénario vide
                <span className="text-2xl">→</span>
              </div>
            </button>

            <button
              onClick={() => handleChooseTemplate('fer_ensemble')}
              className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-green-500 transition-all p-8 text-left group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg group-hover:bg-green-100 transition-colors">
                  <School size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Template Fer Ensemble</h3>
              </div>
              <p className="text-gray-600">
                Utilisez un template pré-configuré pour une école de production (CAP), avec
                dimensions et lignes déjà remplies. Modifiable après création.
              </p>
              <div className="mt-6 text-green-600 font-medium flex items-center gap-2">
                Utiliser ce template
                <span className="text-2xl">→</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => setStep('choose')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Retour
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuration du scénario</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du scénario *
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ex: Budget prévisionnel 2024-2029"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début du premier exercice *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                L'exercice fiscal débutera chaque année à cette date (ex: 1er septembre)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre d'années *
              </label>
              <select
                value={yearsCount}
                onChange={e => setYearsCount(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <option key={n} value={n}>
                    {n} ans
                  </option>
                ))}
              </select>
            </div>

            {templateType === 'fer_ensemble' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <School size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-900 text-sm mb-1">
                      Template École de production
                    </h4>
                    <p className="text-sm text-green-700">
                      Ce scénario sera pré-rempli avec les dimensions (CAP1, CAP2, PostCAP) et
                      des lignes de charges/produits typiques d'une école de production. Vous
                      pourrez les modifier après création.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setStep('choose')}
                disabled={creating}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Retour
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !name.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Création...' : 'Créer le scénario'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
